import { useState } from "react";
import { DoctorLayout } from "@/components/layout/doctor-layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronUp, Info } from "lucide-react";

interface CriteriaEntry {
  id: string;
  condition: string;
  system: string;
  year: number;
  sensitivity: string;
  specificity: string;
  threshold: string;
  criteria: { domain: string; items: string[] }[];
  notes: string;
  source: string;
}

const CRITERIA_DATA: CriteriaEntry[] = [
  {
    id: "ra",
    condition: "Rheumatoid Arthritis",
    system: "2010 ACR/EULAR Classification Criteria",
    year: 2010,
    sensitivity: "≥70%",
    specificity: "≥80%",
    threshold: "Score ≥6/10 = definite RA",
    source: "Aletaha D et al. Arthritis Rheum. 2010",
    notes: "Applies to patients with ≥1 swollen joint not better explained by another diagnosis. Excludes erosive disease typical of established RA.",
    criteria: [
      {
        domain: "A. Joint involvement (0–5)",
        items: [
          "1 large joint — 0",
          "2–10 large joints — 1",
          "1–3 small joints (large joints not counted) — 2",
          "4–10 small joints (large joints not counted) — 3",
          ">10 joints (at least 1 small joint) — 5",
        ],
      },
      {
        domain: "B. Serology (0–3)",
        items: [
          "Negative RF AND negative anti-CCP — 0",
          "Low-positive RF OR low-positive anti-CCP — 2",
          "High-positive RF OR high-positive anti-CCP — 3",
        ],
      },
      {
        domain: "C. Acute-phase reactants (0–1)",
        items: ["Normal CRP AND normal ESR — 0", "Abnormal CRP OR abnormal ESR — 1"],
      },
      {
        domain: "D. Duration of symptoms (0–1)",
        items: ["<6 weeks — 0", "≥6 weeks — 1"],
      },
    ],
  },
  {
    id: "sle",
    condition: "Systemic Lupus Erythematosus",
    system: "2019 EULAR/ACR Classification Criteria",
    year: 2019,
    sensitivity: "96.1%",
    specificity: "93.4%",
    threshold: "Score ≥10 (with ANA ≥1:80 as entry criterion)",
    source: "Aringer M et al. Ann Rheum Dis. 2019",
    notes: "ANA ≥1:80 on HEp-2 cells is mandatory entry criterion. Criteria apply if not better explained by another diagnosis.",
    criteria: [
      {
        domain: "Constitutional",
        items: ["Fever — 2"],
      },
      {
        domain: "Haematological",
        items: [
          "Leucopenia — 3",
          "Thrombocytopenia — 4",
          "Autoimmune haemolysis — 4",
        ],
      },
      {
        domain: "Neuropsychiatric",
        items: [
          "Delirium — 2",
          "Psychosis — 3",
          "Seizure — 5",
        ],
      },
      {
        domain: "Mucocutaneous",
        items: [
          "Non-scarring alopecia — 2",
          "Oral ulcers — 2",
          "Subacute cutaneous or discoid lupus — 4",
          "Acute cutaneous lupus — 6",
        ],
      },
      {
        domain: "Serosal",
        items: [
          "Pleural or pericardial effusion — 5",
          "Acute pericarditis — 6",
        ],
      },
      {
        domain: "Musculoskeletal",
        items: ["Joint involvement (synovitis ≥2 joints or ≥30 min morning stiffness) — 6"],
      },
      {
        domain: "Renal",
        items: [
          "Proteinuria >0.5 g/24h — 4",
          "Renal biopsy Class II or V lupus nephritis — 8",
          "Renal biopsy Class III or IV lupus nephritis — 10",
        ],
      },
      {
        domain: "Antiphospholipid antibodies",
        items: ["Anti-cardiolipin OR anti-β2GP1 OR lupus anticoagulant — 2"],
      },
      {
        domain: "Complement proteins",
        items: ["Low C3 OR low C4 — 3", "Low C3 AND low C4 — 4"],
      },
      {
        domain: "SLE-specific antibodies",
        items: ["Anti-dsDNA OR anti-Sm — 6"],
      },
    ],
  },
  {
    id: "sjogrens",
    condition: "Primary Sjögren's Syndrome",
    system: "2016 ACR/EULAR Classification Criteria",
    year: 2016,
    sensitivity: "96%",
    specificity: "95%",
    threshold: "Score ≥4 = primary SS (with ocular/oral symptoms as entry criterion)",
    source: "Shiboski CH et al. Arthritis Rheumatol. 2017",
    notes: "Entry criterion: positive response to ≥1 of the ESSDAI ocular/oral symptom questions. Each criterion only counted once.",
    criteria: [
      {
        domain: "Biopsy and serology (weighted)",
        items: [
          "Lip salivary gland biopsy with focal lymphocytic sialadenitis and focus score ≥1 foci/4mm² — 3",
          "Anti-SSA/Ro positive — 3",
        ],
      },
      {
        domain: "Ocular signs",
        items: [
          "Ocular Staining Score ≥5 (or van Bijsterveld ≥4) in at least one eye — 1",
          "Schirmer's test ≤5 mm/5 min in at least one eye — 1",
        ],
      },
      {
        domain: "Salivary gland function",
        items: ["Unstimulated whole saliva flow rate ≤0.1 mL/min — 1"],
      },
    ],
  },
  {
    id: "ssc",
    condition: "Systemic Sclerosis (SSc)",
    system: "2013 ACR/EULAR Classification Criteria",
    year: 2013,
    sensitivity: "91%",
    specificity: "92%",
    threshold: "Score ≥9 = definite SSc",
    source: "van den Hoogen F et al. Ann Rheum Dis. 2013",
    notes: "Not applicable to patients with skin thickening sparing the fingers, or with SSc-like disorder better explaining findings.",
    criteria: [
      {
        domain: "Skin thickening",
        items: [
          "Skin thickening of fingers extending proximal to MCPJs (sufficient criterion) — 9",
          "Puffy fingers — 2",
          "Sclerodactyly (distal to MCPJs but proximal to PIPJs) — 4",
        ],
      },
      {
        domain: "Fingertip lesions",
        items: [
          "Digital tip ulcers — 2",
          "Fingertip pitting scars — 3",
        ],
      },
      {
        domain: "Telangiectasia — 2",
        items: [],
      },
      {
        domain: "Abnormal nailfold capillaries — 2",
        items: [],
      },
      {
        domain: "Pulmonary arterial hypertension and/or ILD — 2",
        items: [],
      },
      {
        domain: "Raynaud's phenomenon — 3",
        items: [],
      },
      {
        domain: "SSc-related antibodies",
        items: ["Anti-centromere, anti-topoisomerase I (Scl-70), or anti-RNA polymerase III — 3"],
      },
    ],
  },
  {
    id: "myositis",
    condition: "Idiopathic Inflammatory Myopathy",
    system: "2017 EULAR/ACR Classification Criteria",
    year: 2017,
    sensitivity: "87%",
    specificity: "82%",
    threshold: "Probability thresholds: ≥55% possible, ≥75% probable, ≥90% definite IIM",
    source: "Lundberg IE et al. Ann Rheum Dis. 2017",
    notes: "Scoring depends on whether muscle biopsy is available. Web-based calculator recommended for accurate probability calculation.",
    criteria: [
      {
        domain: "Clinical — constitutional",
        items: ["Proximal upper extremity weakness — included in model"],
      },
      {
        domain: "Skin manifestations",
        items: [
          "Heliotrope rash",
          "Gottron's papules",
          "Gottron's sign",
        ],
      },
      {
        domain: "Muscle",
        items: [
          "Proximal leg weakness",
          "Neck flexor weakness",
          "Leg proximal and distal weakness",
        ],
      },
      {
        domain: "Laboratories",
        items: [
          "CK or LDH or AST or ALT >ULN",
          "Anti-Jo-1 (anti-histidyl-tRNA synthetase) antibody",
          "MRI: increased signal in muscle",
        ],
      },
      {
        domain: "Biopsy (if available)",
        items: [
          "Endomysial infiltration of mononuclear cells surrounding, not invading myofibres",
          "Perimysial and/or perivascular infiltration of mononuclear cells",
          "Perifascicular atrophy",
          "Rimmed vacuoles",
        ],
      },
    ],
  },
  {
    id: "gout",
    condition: "Gout",
    system: "2015 ACR/EULAR Classification Criteria",
    year: 2015,
    sensitivity: "92%",
    specificity: "89%",
    threshold: "Score ≥8 = gout (entry criterion: ≥1 episode of swollen/tender/erythematous peripheral joint/bursa)",
    source: "Neogi T et al. Ann Rheum Dis. 2015",
    notes: "MSU crystal identification in synovial fluid remains the gold standard. Negative MSU on aspirate = −4 (sufficient to exclude classification).",
    criteria: [
      {
        domain: "Clinical",
        items: [
          "Ankle/midfoot (mono/oligoarticular without 1st MTP) — 1",
          "1st MTP involvement — 2",
          "Erythema over joint (reported/observed) — 1",
          "Can't bear touch/pressure — 1",
          "Great difficulty walking/inability to use joint — 2",
          "Time to max pain <24h — 2",
          "Resolution within 14 days — 2",
          "Complete resolution between symptomatic episodes — 2",
          "≥2 typical episodes — 3",
        ],
      },
      {
        domain: "Lab",
        items: [
          "Serum urate <4 mg/dL (0.24 mmol/L) — −4",
          "Serum urate 4–<6 mg/dL — 0",
          "Serum urate 6–<8 mg/dL — 2",
          "Serum urate 8–<10 mg/dL — 3",
          "Serum urate ≥10 mg/dL (0.60 mmol/L) — 4",
          "Negative MSU on synovial fluid aspirate — −2",
        ],
      },
      {
        domain: "Imaging",
        items: [
          "Urate deposition on US (double contour sign) or DECT — 4",
          "Gout-related joint damage on X-ray — 4",
        ],
      },
    ],
  },
  {
    id: "axspa",
    condition: "Axial Spondyloarthritis",
    system: "2009 ASAS Classification Criteria for axSpA",
    year: 2009,
    sensitivity: "82.9%",
    specificity: "84.4%",
    threshold: "Sacroiliitis on imaging + ≥1 SpA feature OR HLA-B27 + ≥2 SpA features",
    source: "Rudwaleit M et al. Ann Rheum Dis. 2009",
    notes: "Entry criterion: back pain ≥3 months, onset <45 years. Sacroiliitis on imaging = MRI (active inflammation) or definite radiographic sacroiliitis by modified New York criteria.",
    criteria: [
      {
        domain: "SpA features",
        items: [
          "Inflammatory back pain",
          "Arthritis",
          "Enthesitis (heel)",
          "Uveitis",
          "Dactylitis",
          "Psoriasis",
          "Crohn's/colitis",
          "Good response to NSAIDs",
          "Family history of SpA",
          "HLA-B27",
          "Elevated CRP",
        ],
      },
      {
        domain: "Imaging arm",
        items: ["Sacroiliitis on MRI (active/acute inflammation) OR definite radiographic sacroiliitis (mNY criteria) — PLUS ≥1 SpA feature"],
      },
      {
        domain: "Clinical arm",
        items: ["HLA-B27 positive — PLUS ≥2 SpA features (from list above)"],
      },
    ],
  },
  {
    id: "vasculitis-gca",
    condition: "Giant Cell Arteritis",
    system: "1990 ACR Classification Criteria",
    year: 1990,
    sensitivity: "93.5%",
    specificity: "91.2%",
    threshold: "≥3 of 5 criteria (sensitivity 93.5%, specificity 91.2% vs other vasculitides)",
    source: "Hunder GG et al. Arthritis Rheum. 1990",
    notes: "Developed for classification vs other vasculitis. ACR/EULAR 2022 criteria now available and preferred for newer studies (web calculator available).",
    criteria: [
      {
        domain: "1990 ACR Criteria (≥3 required)",
        items: [
          "Age at disease onset ≥50 years",
          "New headache (new onset or new type of localised pain in the head)",
          "Temporal artery abnormality (tenderness or decreased pulsation, unrelated to arteriosclerosis of cervical arteries)",
          "Elevated ESR ≥50 mm/hour by Westergren method",
          "Abnormal artery biopsy (showing vasculitis with mononuclear cell infiltration or granulomatous inflammation, with or without multinucleated giant cells)",
        ],
      },
    ],
  },
];

function CriteriaCard({ entry }: { entry: CriteriaEntry }) {
  const [open, setOpen] = useState(false);

  return (
    <Card className="overflow-hidden">
      <button
        className="w-full text-left"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <CardHeader className="flex flex-row items-start justify-between gap-4 pb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <CardTitle className="text-base">{entry.condition}</CardTitle>
              <Badge variant="secondary" className="text-xs">{entry.year}</Badge>
            </div>
            <p className="text-sm text-slate-500">{entry.system}</p>
          </div>
          {open ? (
            <ChevronUp className="h-5 w-5 text-slate-400 shrink-0 mt-0.5" />
          ) : (
            <ChevronDown className="h-5 w-5 text-slate-400 shrink-0 mt-0.5" />
          )}
        </CardHeader>
      </button>

      {open && (
        <CardContent className="pt-0 space-y-4">
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="bg-indigo-50 rounded-xl p-3 text-center">
              <p className="text-xs text-indigo-600 font-semibold uppercase tracking-wide mb-1">Sensitivity</p>
              <p className="font-bold text-indigo-900">{entry.sensitivity}</p>
            </div>
            <div className="bg-violet-50 rounded-xl p-3 text-center">
              <p className="text-xs text-violet-600 font-semibold uppercase tracking-wide mb-1">Specificity</p>
              <p className="font-bold text-violet-900">{entry.specificity}</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3 text-center">
              <p className="text-xs text-slate-600 font-semibold uppercase tracking-wide mb-1">Classification</p>
              <p className="font-bold text-slate-900 text-xs leading-snug">{entry.threshold}</p>
            </div>
          </div>

          <div className="space-y-3">
            {entry.criteria.map((section, i) => (
              <div key={i}>
                <p className="text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">{section.domain}</p>
                {section.items.length > 0 && (
                  <ul className="space-y-1">
                    {section.items.map((item, j) => (
                      <li key={j} className="text-sm text-slate-600 flex items-start gap-2">
                        <span className="text-indigo-400 mt-0.5">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2">
            <Info className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-amber-800 font-semibold mb-0.5">Clinical note</p>
              <p className="text-xs text-amber-700">{entry.notes}</p>
              <p className="text-xs text-amber-600 mt-1 font-medium">{entry.source}</p>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

export default function DiagnosticCriteria() {
  const [search, setSearch] = useState("");
  const filtered = CRITERIA_DATA.filter(
    (c) =>
      c.condition.toLowerCase().includes(search.toLowerCase()) ||
      c.system.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <DoctorLayout title="Diagnostic Criteria">
      <div className="mb-6">
        <p className="text-slate-600 mb-4">
          ACR/EULAR classification criteria for the major rheumatic conditions. Classification criteria are developed for research cohorts — they support but do not replace clinical diagnosis.
        </p>
        <input
          type="text"
          placeholder="Search conditions..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-md border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-300"
        />
      </div>

      <div className="space-y-3">
        {filtered.length === 0 ? (
          <p className="text-slate-500 text-sm">No matching conditions found.</p>
        ) : (
          filtered.map((entry) => <CriteriaCard key={entry.id} entry={entry} />)
        )}
      </div>
    </DoctorLayout>
  );
}
